import { useMemo } from 'react'
import { useCollectionData } from 'react-firebase-hooks/firestore'

import { useQuery } from '@tanstack/react-query'
import {
  collectionGroup,
  type FirestoreDataConverter,
  getDocs,
  getFirestore,
  orderBy,
  query,
  Timestamp,
  where,
} from 'firebase/firestore'
import { z } from 'zod'

import { ErrorWithSentryCaptureContext } from '@/sentry/ErrorWithSentryCaptureContext.ts'

// Zod schemas (same as configurationHistoryCollection.ts but with regionId)
const ChangedByZod = z.object({
  uid: z.string(),
  username: z.string(),
})

const BaseConfigurationHistoryEventZod = z.object({
  timestamp: z.instanceof(Timestamp).transform((v) => v.toDate()),
  changedBy: ChangedByZod,
  eveningId: z.number().optional(),
  operatingDate: z.string().optional(),
})

const AssignmentHistoryEventZod = BaseConfigurationHistoryEventZod.extend({
  type: z.literal('assignment'),
  previousValue: z.enum(['manual', 'trf', 'solver', 'legacy']),
  newValue: z.enum(['manual', 'trf', 'solver', 'legacy']),
})

const MobileRequestsHistoryEventZod = BaseConfigurationHistoryEventZod.extend({
  type: z.literal('mobileRequests'),
  previousValue: z.boolean(),
  newValue: z.boolean(),
  source: z.enum(['central', 'evening']).optional(),
})

const WaitTimeHistoryEventZod = BaseConfigurationHistoryEventZod.extend({
  type: z.literal('waitTime'),
  previousValue: z.enum(['default', 'increased']),
  newValue: z.enum(['default', 'increased']),
})

const AssistedAssignmentHistoryEventZod = BaseConfigurationHistoryEventZod.extend({
  type: z.literal('assistedAssignment'),
  previousValue: z.boolean(),
  newValue: z.boolean(),
})

const ConfigurationHistoryEventZod = z.discriminatedUnion('type', [
  AssignmentHistoryEventZod,
  MobileRequestsHistoryEventZod,
  WaitTimeHistoryEventZod,
  AssistedAssignmentHistoryEventZod,
])

export type AllCentralsConfigurationHistoryEvent = z.infer<typeof ConfigurationHistoryEventZod> & {
  _id: string
  regionId: string
}

/**
 * Converter that extracts regionId from the document path.
 * Path format: regions/{regionId}/configurationHistory/{docId}
 */
const AllCentralsConfigurationHistoryConverter: FirestoreDataConverter<AllCentralsConfigurationHistoryEvent> = {
  toFirestore: (data) => data,
  fromFirestore: (snapshot, options) => {
    const result = ConfigurationHistoryEventZod.safeParse(snapshot.data(options))
    if (!result.success) {
      throw new ErrorWithSentryCaptureContext(
        `Invalid configuration history event ${snapshot.id}`,
        {
          tags: { 'firestore.zod': 'allCentralsConfigurationHistory' },
          fingerprint: ['firestore', 'zod', 'allCentralsConfigurationHistory'],
          extra: { data: snapshot.data(options), document: { path: snapshot.ref.path } },
        },
        { cause: result.error }
      )
    }
    // Extract regionId from document path: regions/{regionId}/configurationHistory/{docId}
    const regionId = snapshot.ref.parent.parent?.id ?? 'unknown'
    return { _id: snapshot.id, regionId, ...result.data }
  },
}

/**
 * Collection group query for configurationHistory across all regions.
 */
export const allCentralsConfigurationHistoryQuery = (operatingDate: string) =>
  query(
    collectionGroup(getFirestore(), 'configurationHistory').withConverter(AllCentralsConfigurationHistoryConverter),
    where('operatingDate', '==', operatingDate),
    orderBy('timestamp', 'asc')
  )

export interface UseAllCentralsConfigurationHistoryOptions {
  operatingDate: string | undefined
  isLive: boolean
}

/**
 * Query options for TanStack Query (historical data).
 */
export const allCentralsConfigurationHistoryQueryOptions = (operatingDate: string | undefined) => ({
  queryKey: ['allCentralsConfigurationHistory', operatingDate] as const,
  queryFn: async () => {
    if (!operatingDate) return []
    const queryRef = allCentralsConfigurationHistoryQuery(operatingDate)
    const snapshot = await getDocs(queryRef)
    return snapshot.docs.map((doc) => doc.data())
  },
  staleTime: Infinity, // Historical data doesn't change
  enabled: !!operatingDate,
})

/**
 * Hook to fetch configuration history across all centrals.
 * - For live: uses Firestore snapshot listener (real-time updates)
 * - For historical: uses TanStack Query (one-time read with caching)
 *
 * Errors are surfaced via the `error` return value and can be displayed to the user.
 */
export const useAllCentralsConfigurationHistory = ({
  operatingDate,
  isLive,
}: UseAllCentralsConfigurationHistoryOptions) => {
  // For live evenings: use Firestore snapshot listener
  const liveQuery = useMemo(
    () => (isLive && operatingDate ? allCentralsConfigurationHistoryQuery(operatingDate) : null),
    [isLive, operatingDate]
  )
  const [liveData, liveLoading, liveError] = useCollectionData<AllCentralsConfigurationHistoryEvent>(liveQuery)

  // For historical evenings: use TanStack Query (one-time read)
  const {
    data: historicalData = [],
    isLoading: historicalLoading,
    error: historicalError,
  } = useQuery({
    ...allCentralsConfigurationHistoryQueryOptions(operatingDate),
    enabled: !isLive && !!operatingDate,
  })

  // Combine the data sources based on mode
  const events = isLive ? (liveData ?? []) : historicalData
  const isLoading = isLive ? liveLoading : historicalLoading
  const error = isLive ? liveError : historicalError

  return { events, isLoading, error }
}
